/**
 * Offline-fähiger Daten-Layer für Schichten.
 *
 * Lese-Hooks: lesen aus IndexedDB live (Dexie-React-Hooks), die Sync-Engine
 * aktualisiert die DB im Hintergrund. Dadurch funktionieren Lese-Operationen
 * vollständig offline.
 *
 * Mutations: legen optimistisch in IndexedDB an, hängen die Operation an die
 * Outbox. Wenn online → Sync wird sofort getriggert.
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { db, type LocalShift } from '../db/schema';
import * as outbox from '../db/outbox';
import type { CreateShiftInput, UpdateShiftInput } from '../lib/shifts';
import { shiftsApi } from '../lib/shifts';
import { useAuth } from '../store/auth';

/** Live-Query: alle Schichten in einem Monat ("YYYY-MM") für den eingeloggten User. */
export function useShiftsForMonth(month: string): LocalShift[] | undefined {
  const me = useAuth((s) => s.user);
  return useLiveQuery(
    async () => {
      if (!me) return [];
      const start = `${month}-01`;
      // Letzter Tag des Monats
      const [y, m] = month.split('-').map(Number);
      const endDate = new Date(Date.UTC(y!, m!, 0)).toISOString().slice(0, 10);
      return db.shifts
        .where('userId')
        .equals(me.id)
        .and((s) => !s.deletedAt && s.shiftDate >= start && s.shiftDate <= endDate)
        .sortBy('shiftDate');
    },
    [me?.id, month],
    [],
  );
}

/**
 * Live-Query: die aktuell „offen laufende" Schicht des eingeloggten Users —
 * also eine Schicht von heute, die `isOpenEnd=true` hat und deren `endTime`
 * noch nicht eingetragen wurde (= Platzhalter, endTime === startTime).
 *
 * Liefert `undefined` solange noch nicht geladen, `null` wenn keine aktive
 * Schicht da ist, oder das Schicht-Objekt selbst.
 */
export function useActiveOpenShift(): LocalShift | null | undefined {
  const me = useAuth((s) => s.user);
  return useLiveQuery(
    async () => {
      if (!me) return null;
      // Gesamtprüfung 11.09.2026: LOKALES Datum (wie beim Einstempeln) statt
      // UTC — sonst fehlte die Leiste nach Mitternacht bis 01:00/02:00 Uhr.
      // Eine offene Schicht von GESTERN (Nachtschicht über Mitternacht) zählt
      // weiter, damit man sie noch ausstempeln kann.
      const now = new Date();
      const iso = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const today = iso(now);
      const yesterday = iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
      const shifts = await db.shifts
        .where('userId')
        .equals(me.id)
        .and(
          (s) =>
            !s.deletedAt &&
            (s.shiftDate === today || s.shiftDate === yesterday) &&
            s.isOpenEnd === true &&
            s.startTime === s.endTime &&
            s.status !== 'cancelled',
        )
        .toArray();
      // Neueste zuerst (heute vor gestern, spätere Startzeit zuerst).
      shifts.sort((a, b) => (b.shiftDate + b.startTime).localeCompare(a.shiftDate + a.startTime));
      return shifts[0] ?? null;
    },
    [me?.id],
    undefined,
  );
}

/** Live-Query: alle Schichten in einer Datums-Range. */
export function useShiftsInRange(fromIso: string, toIso: string): LocalShift[] | undefined {
  const me = useAuth((s) => s.user);
  return useLiveQuery(
    async () => {
      if (!me) return [];
      return db.shifts
        .where('userId')
        .equals(me.id)
        .and((s) => !s.deletedAt && s.shiftDate >= fromIso && s.shiftDate <= toIso)
        .sortBy('shiftDate');
    },
    [me?.id, fromIso, toIso],
    [],
  );
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

function genUuid(): string {
  // Browser-natives crypto.randomUUID() ist seit ~2022 überall verfügbar (HTTPS-Requirement).
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback (unsicher, nur als letzte Option).
  // Gesamtprüfung 11.09.2026: Fallback als gültige UUID v4 (der Server
  // verlangt eine UUID — „tmp-…“ wurde mit 400 abgelehnt und ging verloren).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function useCreateShift() {
  const me = useAuth((s) => s.user);
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateShiftInput) => {
      if (!me) throw new Error('Nicht eingeloggt');
      const tempId = genUuid();
      const now = new Date().toISOString();
      const isSelfEntry = !input.userId || input.userId === me.id;
      const optimisticRequestedBonus = isSelfEntry
        ? Math.max(input.requestedBonusHours ?? 0, input.bonusHours ?? 0)
        : input.requestedBonusHours ?? 0;
      const local: LocalShift = {
        id: tempId,
        userId: input.userId ?? me.id,
        locationId: input.locationId,
        templateId: input.templateId ?? null,
        shiftDate: input.shiftDate,
        startTime: input.startTime,
        endTime: input.endTime,
        status: input.status ?? 'planned',
        note: input.note ?? null,
        hourlyWage: input.hourlyWage ?? me.hourlyWage,
        bonusHours: input.bonusHours ?? 0,
        isOpenEnd: input.isOpenEnd ?? false,
        // Phase F (Kapitel 11.2/11.3) — optimistische Default-Annahme, wird
        // beim nächsten Sync durch den echten Server-Stand überschrieben.
        origin: input.userId && input.userId !== me.id ? 'planned' : 'self_reported',
        // Seit 11.09.2026: beim Selbst-Eintrag gehen auch normale Zusatzstunden
        // in die Chef-Freigabe (Server-Regel in shifts.ts POST, Chef selbst
        // ausgenommen — das weiß erst der Server, der nächste Sync korrigiert).
        bonusStatus: optimisticRequestedBonus > 0 ? 'pending' : 'none',
        requestedBonusHours: optimisticRequestedBonus > 0 ? optimisticRequestedBonus : null,
        bonusDecidedBy: null,
        bonusDecidedAt: null,
        vacationOverrideBy: null,
        settledAt: null,
        settledBy: null,
        // Phase M (Kapitel 18) — beim Neuanlegen gibt es noch keine
        // Übergabezeit-Anpassung.
        timeAdjustedBy: null,
        timeAdjustedAt: null,
        originalStartTime: null,
        originalEndTime: null,
        clientUuid: tempId,
        createdAt: now,
        lastModified: now,
        // Phase DD (Kapitel 19.10): optimistische Annahme, wird beim
        // nächsten Sync durch den echten Server-Stand überschrieben.
        lastModifiedBy: me.id,
        deletedAt: null,
        pending: true,
      };
      await db.shifts.put(local);
      await outbox.enqueue('shift.create', tempId, { ...input, clientUuid: tempId });
      return local;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outbox'] });
    },
  });
}

export function useUpdateShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; input: UpdateShiftInput }) => {
      const existing = await db.shifts.get(args.id);
      if (!existing) throw new Error('Schicht nicht im Cache');
      const updated: LocalShift = {
        ...existing,
        ...args.input,
        note: args.input.note === undefined ? existing.note : args.input.note,
        templateId:
          args.input.templateId === undefined ? existing.templateId : args.input.templateId,
        lastModified: new Date().toISOString(),
        pending: true,
      };
      await db.shifts.put(updated);
      // baseLastModified = der Server-Stand, auf dem wir aufsetzen.
      // Bei pending=Schichten verwenden wir den letzten bekannten Server-Stand.
      // Falls die Schicht noch nie gesynct wurde, ist `lastModified` lokal —
      // dann gibt es kein "Konflikt" gegen Server, daher leer lassen.
      const base = existing.pending ? undefined : existing.lastModified;
      await outbox.enqueue('shift.update', args.id, args.input, base);
      return updated;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outbox'] });
    },
  });
}

export function useDeleteShift() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const existing = await db.shifts.get(id);
      if (existing) {
        await db.shifts.put({
          ...existing,
          deletedAt: new Date().toISOString(),
          pending: true,
        });
      }
      const base = existing && !existing.pending ? existing.lastModified : undefined;
      await outbox.enqueue('shift.delete', id, undefined, base);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outbox'] });
    },
  });
}

/**
 * Abrechnung einer einzelnen Schicht (Admin/Manager). Setzt lokal optimistisch
 * `settledAt`/`settledBy`, sendet über die Outbox nur `settledAt` an den Server
 * (settledBy leitet der Server aus dem eingeloggten User ab). Funktioniert
 * offline; wird beim nächsten Sync übertragen.
 */
export function useSettleShift() {
  const me = useAuth((s) => s.user);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; settled: boolean }) => {
      const existing = await db.shifts.get(args.id);
      if (!existing) throw new Error('Schicht nicht im Cache');
      const settledAt = args.settled ? new Date().toISOString() : null;
      const updated: LocalShift = {
        ...existing,
        settledAt,
        settledBy: args.settled ? (me?.id ?? null) : null,
        // Storno einer Tagesgeld-Entnahme (nur Chef, Server prüft) leert den Nachweis.
        ...(args.settled ? {} : { dailyPayoutAt: null, dailyPayoutAmount: null }),
        lastModified: new Date().toISOString(),
        pending: true,
      };
      await db.shifts.put(updated);
      const base = existing.pending ? undefined : existing.lastModified;
      await outbox.enqueue('shift.update', args.id, { settledAt }, base);
      return updated;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outbox'] });
    },
  });
}

/**
 * Phase F (Kapitel 11.6): Zusatzstunden einer Einspringen-Schicht
 * bestaetigen/ablehnen -- ausschliesslich Chef-Rechte. Bewusst KEIN
 * Outbox-Eintrag: das ist eine Live-Server-Entscheidung (braucht ohnehin den
 * aktuellen Stand), kein offline-faehiges Editieren einer eigenen Schicht.
 * Schreibt das Ergebnis direkt in den lokalen Cache, damit die Ansicht
 * sofort aktualisiert ist.
 */
export function useBonusDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; decision: 'approved' | 'rejected'; token: string }) => {
      const saved = await shiftsApi.bonusDecision(args.token, args.id, args.decision);
      await db.shifts.put({ ...saved, pending: false });
      return saved;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outbox'] });
    },
  });
}

/**
 * Phase M (Kapitel 18, Entscheidung #21): Übergabezeit zwischen zwei direkt
 * aufeinanderfolgenden eigenen Schichten verschieben. Bewusst KEIN
 * Outbox-Eintrag (wie `useBonusDecision`): die Server-Validierung
 * (Nachbar-Schicht muss existieren, Öffnungszeiten-Leitplanke) braucht
 * sofortiges Feedback — eine Offline-Warteschlange könnte den Fehler erst
 * viel später melden, wenn der Moment der Übergabe längst vorbei ist.
 * Schreibt das Ergebnis direkt in den lokalen Cache, damit die Ansicht
 * sofort aktualisiert ist.
 */
export function useAdjustHandoverTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; field: 'start' | 'end'; time: string; token: string }) => {
      const saved = await shiftsApi.adjustHandoverTime(args.token, args.id, args.field, args.time);
      await db.shifts.put({ ...saved, pending: false });
      return saved;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outbox'] });
    },
  });
}

/**
 * Nachtrag 11.09.2026: Tagesgehalt der eigenen Schicht aus der Kasse nehmen.
 * Wie useBonusDecision bewusst live (kein Outbox-Eintrag) — der Server rechnet
 * den Betrag und prüft die Freigabe; das Ergebnis landet direkt im Cache.
 */
export function useDailyPayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { id: string; token: string }) => {
      const saved = await shiftsApi.dailyPayout(args.token, args.id);
      await db.shifts.put({ ...saved, pending: false });
      return saved;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['outbox'] });
    },
  });
}

/** Kleiner Helfer um aus React heraus „Sync starten" auszulösen. */
export function useTriggerSyncOnMutation() {
  const qc = useQueryClient();
  return useCallback(() => {
    qc.invalidateQueries({ queryKey: ['outbox'] });
  }, [qc]);
}
